import React, { useState, useMemo } from 'react';
import { Row, FormControl, InputGroup, Button, HelpBlock } from 'react-bootstrap';
import _ from 'lodash';
import InputCollection from '../../../components/InputCollection/InputCollection';
import * as constants from '../../../constants';

function Escalations({
    escalationTypesOptions,
    selectedEscalationTypeId,
    onTypeChange,
    rows,
    onRowsChange,
    isBusy,
    formValidationCheck,
    fallbackEscalationTypeName
}) {
    const typeId = parseInt(selectedEscalationTypeId || 0);
    const escError = formValidationCheck ? formValidationCheck('escalations') : '';
    const [step, setStep] = useState(5);

    const MAX_TO_VALUE = 999; // Admin convenience 'Max' sentinel

    const rowIssues = useMemo(() => {
        if (!(typeId === constants.EscalationTypeEnum.Monthly || typeId === constants.EscalationTypeEnum.Yearly)) return [];
        const inputRows = Array.isArray(rows) ? rows : [];
        const parsedRows = inputRows.map(row => ({ from: parseInt(row.from), to: parseInt(row.to), amount: parseFloat(row.amount) }));
        const issues = [];
        for (let index = 0; index < parsedRows.length; index++) {
            const current = parsedRows[index];
            const rowIssue = { from: '', to: '', amount: '', hint: '' };
            // First row may start at 0 (warning count). Subsequent rows must start >=1.
            if (index === 0) {
                if (isNaN(current.from) || current.from < 0) rowIssue.from = 'First from must be 0 or greater';
            } else if (isNaN(current.from) || current.from < 1) {
                rowIssue.from = 'From must be a number >= 1';
            }
            if (isNaN(current.to) || current.to < 1) rowIssue.to = 'To must be a number >= 1';
            if (!rowIssue.from && !rowIssue.to && current.from > current.to) {
                rowIssue.to = 'To must be >= From';
            }
            // Allow 0 only for first row (warning escalation). Subsequent must be > 0
            if (isNaN(current.amount) || current.amount < 0) {
                rowIssue.amount = 'Amount must be >= 0';
            } else if (index > 0 && current.amount === 0) {
                rowIssue.amount = 'Only first escalation may be 0';
            } else if (index === 0 && current.amount === 0) {
                rowIssue.hint = 'Warning: zero amount (acts as warning)';
            }
            const previous = parsedRows[index - 1];
            if (!rowIssue.from && index === 0 && current.from !== 0) rowIssue.from = 'First range must start at 0';
            if (!rowIssue.from && index > 0 && !isNaN(previous?.to) && current.from !== (previous.to + 1)) rowIssue.from = `Should start at ${previous.to + 1}`;
            issues[index] = rowIssue;
        }
        return issues;
    }, [rows, typeId]);

    const handleAddRow = () => {
        const lastTo = rows && rows.length > 0 ? parseInt(rows[rows.length - 1]?.to || 0) : 0;
        const nextFrom = isNaN(lastTo) || lastTo < 1 ? 1 : lastTo + 1;
        const nextTo = nextFrom + (parseInt(step) > 0 ? parseInt(step) - 1 : 0);
        const next = [...(rows || []), { id: null, from: nextFrom, to: nextTo || '', amount: '' }];
        onRowsChange(next);
    };

    const handleRemoveRow = (index) => {
        const next = (rows || []).filter((_, i) => i !== index);
        onRowsChange(next);
    };

    const updateRow = (index, patch) => {
        const next = (rows || []).map((row, i) => i === index ? { ...row, ...patch } : row);
        onRowsChange(next);
    };

    const normalizeRanges = () => {
        const stepSize = parseInt(step);
        if (isNaN(stepSize) || stepSize < 1) return;
        const rowsCopy = Array.isArray(rows) ? [...rows] : [];
        rowsCopy.sort((a, b) => (parseInt(a.from) || 0) - (parseInt(b.from) || 0));
        let currentFrom = 1;
        const next = rowsCopy.map(row => {
            const normalizedRow = { ...row };
            normalizedRow.from = currentFrom;
            normalizedRow.to = currentFrom + stepSize - 1;
            currentFrom = normalizedRow.to + 1;
            return normalizedRow;
        });
        onRowsChange(next);
    };

    return (
        <>
            <Row>
                <div className="col-md-12 mt-1">
                    <div style={{ borderBottom: '1px solid #b7b7bd', marginBottom: '20px' }}>
                        <h4><strong>Escalations</strong></h4>
                    </div>
                </div>
            </Row>

            <Row>
                <div className="col-md-4">
                    <InputCollection labelText="Escalation Type">
                        {(escalationTypesOptions && escalationTypesOptions.length > 0) ? (
                            <FormControl
                                id="escalationTypeId"
                                componentClass="select"
                                onChange={(e) => onTypeChange(e.target.value)}
                                value={selectedEscalationTypeId ? String(selectedEscalationTypeId) : ''}
                                disabled={isBusy}
                            >
                                {escalationTypesOptions}
                            </FormControl>
                        ) : fallbackEscalationTypeName ? (
                            <FormControl type="text" disabled value={fallbackEscalationTypeName} />
                        ) : (
                            <div className="skeleton skeleton-select" />
                        )}
                    </InputCollection>
                </div>

                {/* Never: single Amount */}
                {typeId === constants.EscalationTypeEnum.Never && (
                    <div className="col-md-4">
                        <InputCollection labelText="Amount" errorMessage={escError}>
                            <InputGroup>
                                <InputGroup.Addon>$</InputGroup.Addon>
                                <FormControl
                                    id="escalation-amount-never"
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    onWheel={(e) => e.target.blur()}
                                    onChange={(e) => {
                                        const amount = e.target.value;
                                        const next = [{ id: rows?.[0]?.id ?? null, from: 1, to: 1, amount: amount }];
                                        onRowsChange(next);
                                    }}
                                    value={(rows?.[0]?.amount) || ''}
                                    disabled={isBusy}
                                />
                            </InputGroup>
                        </InputCollection>
                    </div>
                )}
            </Row>

            {/* Monthly/Yearly grid */}
            {(typeId === constants.EscalationTypeEnum.Monthly || typeId === constants.EscalationTypeEnum.Yearly) && (
                <>
                    <Row>
                        <div className="col-md-12">
                            <Button bsStyle="primary" disabled={isBusy} onClick={handleAddRow}>
                                <i className="fa fa-plus"></i> Add Escalation
                            </Button>
                            <span style={{ marginLeft: 10 }}>
                                <FormControl
                                    type="number"
                                    min="1"
                                    step="1"
                                    style={{ display: 'inline-block', width: 80 }}
                                    value={step}
                                    onWheel={(e) => e.target.blur()}
                                    onChange={(e) => setStep(e.target.value)}
                                    disabled={isBusy}
                                />
                                <span style={{ marginLeft: 5 }}>Step</span>
                            </span>
                            <Button bsStyle="default" style={{ marginLeft: 10 }} disabled={isBusy || !(rows && rows.length)} onClick={normalizeRanges}>
                                Normalize
                            </Button>
                            {!_.isEmpty(escError) && (
                                <div style={{ display: 'inline-block', marginLeft: 15 }}>
                                    <HelpBlock><i className="fa fa-exclamation-circle"></i>&nbsp;{escError}</HelpBlock>
                                </div>
                            )}
                        </div>
                    </Row>
                    {(!rows || rows.length === 0) && (
                        <Row>
                            <div className="col-md-12" style={{ color: '#666', fontStyle: 'italic', marginTop: '10px' }}>
                                No escalations added.
                            </div>
                        </Row>
                    )}
                    {(rows || []).map((row, index) => (
                        <Row key={`escalation-${index}`}>
                            <div className="col-md-3">
                                <InputCollection labelText="From">
                                    <FormControl
                                        type="number"
                                        step="1"
                                        min="1"
                                        value={row.from}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={(e) => updateRow(index, { from: e.target.value })}
                                        disabled={isBusy}
                                    />
                                    {!_.isEmpty(rowIssues[index]?.from) && <HelpBlock><i className="fa fa-exclamation-circle"></i>&nbsp;{rowIssues[index].from}</HelpBlock>}
                                </InputCollection>
                            </div>
                            <div className="col-md-3">
                                <InputCollection labelText="To">
                                    <FormControl
                                        type="number"
                                        step="1"
                                        min="1"
                                        value={row.to}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={(e) => updateRow(index, { to: e.target.value })}
                                        disabled={isBusy}
                                    />
                                    {!_.isEmpty(rowIssues[index]?.to) && <HelpBlock><i className="fa fa-exclamation-circle"></i>&nbsp;{rowIssues[index].to}</HelpBlock>}
                                    <div style={{ marginTop: 4 }}>
                                        <Button
                                            bsStyle="link"
                                            style={{ padding: 0, fontSize: 12 }}
                                            disabled={isBusy || parseInt(row.to) === MAX_TO_VALUE}
                                            onClick={() => updateRow(index, { to: MAX_TO_VALUE })}
                                        >Set Max ({MAX_TO_VALUE})</Button>
                                    </div>
                                </InputCollection>
                            </div>
                            <div className="col-md-4">
                                <InputCollection labelText="Amount">
                                    <InputGroup>
                                        <InputGroup.Addon>$</InputGroup.Addon>
                                        <FormControl
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            value={row.amount}
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) => updateRow(index, { amount: e.target.value })}
                                            disabled={isBusy}
                                        />
                                    </InputGroup>
                                    {!_.isEmpty(rowIssues[index]?.amount) && <HelpBlock><i className="fa fa-exclamation-circle"></i>&nbsp;{rowIssues[index].amount}</HelpBlock>}
                                    {_.isEmpty(rowIssues[index]?.amount) && !_.isEmpty(rowIssues[index]?.hint) && (
                                        <HelpBlock style={{ color: '#888' }}><i className="fa fa-info-circle"></i>&nbsp;{rowIssues[index].hint}</HelpBlock>
                                    )}
                                </InputCollection>
                            </div>
                            <div className="col-md-2" style={{ marginTop: 25 }}>
                                <Button bsStyle="default" disabled={isBusy} onClick={() => handleRemoveRow(index)}>
                                    Remove
                                </Button>
                            </div>
                        </Row>
                    ))}
                </>
            )}
        </>
    );
}

export default Escalations;
